// SabiasQue.jsx — sección "¿Sabías que?", el problema en datos + cobertura regional.

import React from 'react'
import { trackCta } from '../../lib/analytics.js'

function CoverageRow({ city, ok, time, note }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 0",
        borderBottom: "1px dashed var(--line)",
      }}
    >
      <span
        style={{
          width: 16, height: 16, borderRadius: "50%",
          background: ok ? "var(--gold)" : "transparent",
          border: ok ? "none" : `1.5px solid var(--alert)`,
          display: "grid", placeItems: "center",
          flexShrink: 0,
        }}
      >
        {ok ? (
          <svg width="9" height="9" viewBox="0 0 9 9">
            <path d="M 1.5 4.5 L 3.5 6.5 L 7.5 2" stroke="#0b0a08" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <line x1="1.5" y1="1.5" x2="6.5" y2="6.5" stroke="var(--alert)" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="6.5" y1="1.5" x2="1.5" y2="6.5" stroke="var(--alert)" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        )}
      </span>
      <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: ok ? "var(--fg)" : "var(--fg-mute)" }}>
        {city}
      </div>
      <div style={{ fontSize: 11.5, color: ok ? "var(--fg-dim)" : "var(--alert)", fontFamily: "var(--mono)", letterSpacing: "0.02em", textAlign: "right" }}>
        {note || time}
      </div>
    </div>
  );
}

function SabiasQue() {
  const facts = [
    { v: "12+",     l: "Años sin escala mecánica en Quilpué" },
    { v: "55",      l: "Torres de 10 a 18 pisos en la ciudad" },
    { v: "32 m",    l: "Alcance del nuevo carro proyectado" },
    { v: "350K",    l: "Personas que dependen del servicio" },
  ];
  return (
    <section id="por-que" style={{ borderTop: "1px solid var(--line)" }}>
      <div className="wrap">
        <div className="section-head fade-up">
          <div className="eyebrow"><span className="dot" />¿Sabías qué?</div>
          <h2 className="h-section" style={{ marginTop: 14, lineHeight: "calc(0.92em + 12px)" }}>
            Quilpué <span style={{ color: "var(--alert)" }}>no tiene</span> escala mecánica
          </h2>
        </div>

        <div className="fact-grid">
          {facts.map((f, i) => (
            <div
              key={i}
              className="fact-cell fade-up"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="big-counter tabular fact-value">{f.v}</div>
              <div className="kicker" style={{ marginTop: 6, lineHeight: 1.4 }}>{f.l}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 56, alignItems: "start" }} className="hero-grid">
          <div>
            <div className="num-card">
              <span className="n">01</span>
              <div>
                <h3>Más de una década sin escala mecánica</h3>
                <p>
                  La Segunda Compañía mantiene la especialidad en altura desde hace más de
                  <strong> 30 años</strong>. Históricamente contó con unidades que daban cobertura a
                  Quilpué y a las comunas vecinas. Hoy, la <strong>especialidad sigue, pero sin material mayor</strong>.
                </p>
              </div>
            </div>
            <div className="num-card">
              <span className="n">02</span>
              <div>
                <h3>Una herramienta para más que altura</h3>
                <p>
                  Una escala mecánica no solo rescata personas en pisos altos. También permite atacar
                  <strong> incendios industriales</strong> que requieren gran caudal de agua a
                  distancia, <strong>trabajos en techos</strong> y <strong>rescates en zonas
                  elevadas</strong> — desde personas atrapadas en obras hasta animales en árboles.
                  Es una capacidad operativa que hoy Quilpué simplemente no tiene.
                </p>
              </div>
            </div>
            <div className="num-card">
              <span className="n">03</span>
              <div>
                <h3>La ayuda más cercana llega tarde</h3>
                <p>
                  Quilpué depende hoy de escalas mecánicas ubicadas en comunas vecinas. En el mejor
                  de los casos, la respuesta supera los <strong>20 minutos</strong> en horario de alta
                  congestión. En un incendio en altura, cada minuto es <em>decisivo</em>.
                </p>
              </div>
            </div>
          </div>
          <div className="sticky-panel">
            <div className="card" style={{ padding: 28 }}>
              <div className="kicker" style={{ marginBottom: 14 }}>Cobertura aérea en la región</div>
              <div style={{ fontFamily: "var(--display)", fontSize: 22, lineHeight: 1.05, textTransform: "uppercase", marginBottom: 22 }}>
                ¿Qué comunas tienen escala mecánica?
              </div>

              <div style={{ marginBottom: 22 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 11.5, color: "var(--fg-dim)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                  Provincia de Marga Marga
                </div>
                <CoverageRow city="Limache"        ok note="Tiene escala" />
                <CoverageRow city="Quilpué"        ok={false} note="NO TIENE · esta campaña" />
                <CoverageRow city="Villa Alemana"  ok={false} note="No tiene" />
                <CoverageRow city="Olmué"          ok={false} note="No tiene" />
              </div>

              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 11.5, color: "var(--fg-dim)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                  Provincia de Valparaíso (vecinas)
                </div>
                <CoverageRow city="Viña del Mar" ok note="Tiene escala" />
                <CoverageRow city="Valparaíso"   ok note="Tiene escala" />
                <CoverageRow city="Concón"       ok note="Tiene escala" />
              </div>

              <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--line)", fontSize: 12.5, color: "var(--fg-dim)", lineHeight: 1.55 }}>
                Cuando hay una emergencia en altura en Quilpué, dependemos de las unidades de comunas
                vecinas, con tiempos que pueden superar los 20 minutos en horario de alta congestión.
              </div>
            </div>
          </div>
        </div>

        {/* CTA dentro de la sección */}
        <div
          className="card fade-up"
          style={{
            marginTop: 56,
            padding: "28px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 18,
            background: "linear-gradient(135deg, rgba(232,169,59,0.12), rgba(230,57,70,0.05))",
            borderColor: "var(--line-2)",
          }}
        >
          <div>
            <div className="kicker" style={{ marginBottom: 6 }}>¿Quieres cambiar esta realidad?</div>
            <div style={{ fontFamily: "var(--display)", fontSize: 26, lineHeight: 1, textTransform: "uppercase" }}>
              Cada aporte <span style={{ color: "var(--gold)" }}>nos permite llegar más alto</span>
            </div>
          </div>
          <a
            href="#como"
            className="btn btn-primary btn-lg"
            onClick={(e) => {
              e.preventDefault();
              trackCta("sabias_donar");
              const el = document.getElementById("como");
              if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 72, behavior: "smooth" });
            }}
          >
            Donar ahora →
          </a>
        </div>
      </div>
    </section>
  );
}

export { SabiasQue };
